import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import Message from '../../components/ui/Message';
import { MerchantAccount } from '../../models/interfaces';
import { MerchantAccountsContext } from '../../context/merchantAccounts/MerchantAccountContext';


const currencyConfig: Record<number, { label: string; badgeClass: string }> = {
    188: { label: 'Colones (CRC)', badgeClass: 'bg-primary-subtle text-primary-emphasis' },
    840: { label: 'Dólares (USD)', badgeClass: 'bg-success-subtle text-success-emphasis' },
};

export const MerchantAccountsPage = () => {
    const { state } = useLocation();
    const merchant = state?.merchant;

    const [buscar, setBuscar] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [accountToDelete, setAccountToDelete] = useState<MerchantAccount | null>(null);

    const { merchantAccounts, banks, accountTypes, loadCatalogs, loadMerchantAccounts, deleteMerchantAccount } = useContext(MerchantAccountsContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (!merchant) return;
        const load = async () => {
            setIsLoading(true);
            try {
                await loadCatalogs();
                await loadMerchantAccounts(merchant.id, 0);
            } catch (e) {
                setError('No se pudieron cargar las cuentas del comercio, intente de nuevo.');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [])

    const getBankName = (bankId: number) =>
        banks.find((bank) => bank.code == bankId)?.description ?? '-';

    const getAccountTypeName = (accountTypeId: number) =>
        accountTypes.find((accountType) => accountType.code == accountTypeId)?.description ?? '-';

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return merchantAccounts;
        return merchantAccounts.filter((account) =>
            [account.accountName, account.ibanAccount, getBankName(account.bankCode)]
                .some(val => String(val ?? '').toLowerCase().includes(term))
        );
    }, [merchantAccounts, buscar, banks]);

    // Sin comercio (ej. acceso directo a la URL) se vuelve al listado
    if (!merchant) return <Navigate to="/merchants" replace />;

    const handleNew = () => {
        navigate("/merchant/account", { state: { merchant } });
    }

    const handleEdit = (merchantAccount: MerchantAccount) => {
        navigate("/merchant/account", { state: { merchant, merchantAccount } });
    }

    const processDelete = async () => {
        const account = accountToDelete;
        setAccountToDelete(null);
        if (!account) return;
        const resp = await deleteMerchantAccount(account);
        if (resp !== "success") {
            setError(resp);
        }
    }

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Cuentas bancarias</h3>
                    <p className="text-muted mb-0">Cuentas de liquidación del comercio <span className="fw-semibold text-body">{merchant.name}</span>.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/merchants')}>
                        <i className="bi bi-arrow-left me-1" /> Volver a comercios
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleNew}>
                        <i className="bi bi-plus-lg me-1" /> Agregar cuenta
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por descripción, IBAN o banco..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                                {buscar && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table id='merchantAccountTable' className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Moneda</th>
                                    <th>Cuenta IBAN</th>
                                    <th>Banco</th>
                                    <th>Tipo de cuenta</th>
                                    <th>Descripción</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={6} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : filteredData.length > 0 ? (
                                    filteredData.map(account => {
                                        const currency = currencyConfig[account.currencyId] ?? { label: String(account.currencyId), badgeClass: 'bg-secondary-subtle text-secondary-emphasis' };
                                        return (
                                            <tr key={`${account.merchantId}-${account.currencyId}`}>
                                                <td><span className={`badge rounded-pill ${currency.badgeClass}`}>{currency.label}</span></td>
                                                <td style={{ fontFamily: 'monospace' }}>{account.ibanAccount}</td>
                                                <td>{getBankName(account.bankCode)}</td>
                                                <td>{getAccountTypeName(account.accountTypeId)}</td>
                                                <td className="text-muted">{account.accountName || '-'}</td>
                                                <td style={{ textAlign: 'center', width: 110 }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        title="Editar cuenta"
                                                        onClick={() => handleEdit(account)}
                                                    >
                                                        <i className="bi bi-pencil" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger ms-1"
                                                        title="Eliminar cuenta"
                                                        onClick={() => setAccountToDelete(account)}
                                                    >
                                                        <i className="bi bi-trash" />
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr><td colSpan={6} className='text-center py-4 text-muted'>
                                        {buscar ? 'No se encontraron cuentas para la búsqueda.' : 'No hay cuentas bancarias configuradas.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}

            {accountToDelete && (
                <Message
                    type="success"
                    title="Atención"
                    message={`¿Está seguro que desea eliminar la cuenta ${accountToDelete.ibanAccount}?`}
                    closeDialog={() => setAccountToDelete(null)}
                    showCancel={true}
                    handleCancel={() => setAccountToDelete(null)}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    }
};
